import React, { useEffect, useState } from 'react'
import { Button, Input, Modal, Typography } from 'antd'
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'
import { actionsApi } from 'api/actions'
import {
  ECampaignChannel,
  ISendBroadcast,
  ITestBroadcastResult
} from 'models/notifications'
import { showServerError } from 'shared/utils/showServerError'
import styles from '../Notifications.module.scss'
import { channelNames } from './broadcast'

const TEST_PHONE_KEY = 'broadcastTestPhone'
const TEST_EMAIL = 'info@horsesmart.store'

type TTestChannel = Exclude<ECampaignChannel, ECampaignChannel.POPUP>

const TEST_TARGETS: Record<TTestChannel, string> = {
  [ECampaignChannel.PUSH]:
    'на ваши устройства, где в приложении выполнен вход в этот аккаунт',
  [ECampaignChannel.BOT]: 'в бот модерации',
  [ECampaignChannel.EMAIL]: `на ${TEST_EMAIL}, тема с пометкой [Тест]`,
  [ECampaignChannel.SMS]: 'на номер ниже, платно'
}

const readStoredPhone = () => {
  try {
    return window.localStorage.getItem(TEST_PHONE_KEY) ?? ''
  } catch {
    return ''
  }
}

const storePhone = (phone: string) => {
  try {
    window.localStorage.setItem(TEST_PHONE_KEY, phone)
  } catch {
    // Без хранилища номер просто придётся ввести снова
  }
}

interface TestBroadcastModalProps {
  // null — модалка закрыта
  payload: ISendBroadcast | null
  onClose: () => void
}

export const TestBroadcastModal: React.FC<TestBroadcastModalProps> = ({
  payload,
  onClose
}) => {
  const [phone, setPhone] = useState(readStoredPhone)
  const [isSending, setIsSending] = useState(false)
  const [results, setResults] = useState<
    ITestBroadcastResult['results'] | null
  >(null)

  useEffect(() => {
    setResults(null)
  }, [payload])

  const channels = (
    [
      payload?.sellers?.push || payload?.users?.push
        ? ECampaignChannel.PUSH
        : null,
      payload?.sellers?.bot ? ECampaignChannel.BOT : null,
      payload?.sellers?.email || payload?.users?.email
        ? ECampaignChannel.EMAIL
        : null,
      payload?.sellers?.sms ? ECampaignChannel.SMS : null
    ] as (TTestChannel | null)[]
  ).filter((key): key is TTestChannel => key !== null)
  const hasSms = channels.includes(ECampaignChannel.SMS)

  const send = async () => {
    if (!payload) return
    const testPhone = phone.replace(/\D/g, '')
    if (hasSms && !/^7\d{10}$/.test(testPhone)) {
      Modal.error({
        title: 'Проверьте телефон',
        content: '11 цифр, начиная с 7: например, 79001234567'
      })
      return
    }

    setIsSending(true)
    try {
      const response = await actionsApi.sendTestBroadcast({
        ...payload,
        test_phone: hasSms ? testPhone : undefined
      })
      setResults(response.data.results)
      if (hasSms) storePhone(testPhone)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Modal
      open={payload !== null}
      title='Отправить себе'
      width={560}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key='close' onClick={onClose}>
          Закрыть
        </Button>,
        <Button
          key='send'
          type='primary'
          loading={isSending}
          disabled={!channels.length}
          onClick={send}
        >
          {results ? 'Отправить ещё раз' : 'Отправить'}
        </Button>
      ]}
    >
      {channels.length ? (
        <ul className={styles.testList}>
          {channels.map((key) => {
            const result = results?.[key]
            return (
              <li key={key}>
                <Typography.Text strong>{channelNames[key]}</Typography.Text> —{' '}
                {TEST_TARGETS[key]}
                {result && (
                  <div className={styles.testResult}>
                    {result.ok ? (
                      <CheckCircleFilled className={styles.testOk} />
                    ) : (
                      <CloseCircleFilled className={styles.testFail} />
                    )}
                    <Typography.Text type={result.ok ? undefined : 'danger'}>
                      {result.detail}
                    </Typography.Text>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      ) : (
        <Typography.Paragraph>
          Себе отправляются пуш, бот, письмо и SMS. Всплывающее окно посмотрите
          в предпросмотре.
        </Typography.Paragraph>
      )}
      {hasSms && (
        <Input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder='79001234567'
          maxLength={18}
          className={styles.testPhone}
        />
      )}
      {payload?.sellers?.popup && channels.length > 0 && (
        <Typography.Paragraph type='secondary' className={styles.testNote}>
          Всплывающее окно себе не отправляется — оно есть в предпросмотре.
        </Typography.Paragraph>
      )}
    </Modal>
  )
}
